import Cookies from 'js-cookie';
import moment from 'moment';
import posthog from 'posthog-js';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import { fetchEnrolment } from '../../../../actions/hub/hubEnrolmentManagementActions';
import { api } from '../../../../api';
import type { Enrolment } from '../../../../api/types/enrolment';
import ErrorBlock from '../../../../common/ErrorBlock';
import EnrolmentLayout from '../../../../common/hub/EnrolmentLayout';
import { getNextUpcomingSessionForEnrolment } from '../../../../selectors/hubSelector';
import {
  getSummerPromo,
  hasIncentiveFebruaryRestartedPack as hasIncentiveFebruaryRestartedPackCheck,
} from '../../../../selectors/summerModeSelector';
import { ELEMENT_TYPES } from '../../../../types/hubEnums';
import { SessionPolicy } from '../../../../types/hubInterfaces';
import { SchoolHolidayTerm } from '../../../../types/hubTypes';
import { COOKIE_SAVE_ENROLMENT } from '../../../../util/constants';
import {
  isEnrolmentActiveCheck,
  isEnrolmentPausedCheck,
  isGroupSessionCheck,
  saveCancellingPeriod,
  willEnrolmentBePausedCheck,
} from '../../../../util/helpers';
import {
  PATH_HUB_CR_CANCEL_ENROLMENT,
  PATH_HUB_CR_CHANGE_CONTENT_FOCUS,
  PATH_HUB_CR_CHANGE_TUTOR,
  PATH_HUB_CR_PAUSE_SESSIONS,
  PATH_HUB_ENROLMENT_CHANGE_SCHEDULE,
  PATH_HUB_ENROLMENT_CHANGE_SCHEDULE_GROUP,
  PATH_HUB_ENROLMENT_MANAGE_PAUSE,
  PATH_HUB_SUPPORT,
} from '../../../../util/pagePath';
import { PostHogEvent } from '../../../../util/posthog';
import { TRACKING_CE } from '../../../../util/trackingClasses';
import { getCurrentOrUpcomingSchoolHoliday } from '../../../../v2/common/helpers/schoolHolidays';
import useCanPauseEnrolment from '../../../../v2/features/pause-enrolment/hooks/useCanPauseEnrolment';
import { IntentIntro } from '../../../IntentIntro';
import { OptionCard, OptionCardContent } from '../../OptionCard';
import { PromoText } from '../../PromoText';
import SummerPromoLegalCopy from '../../SummerMode/SummerPromoLegalCopy';

export interface Props {
  selectedEnrolment: {
    resumeSessionDate?: string;
    timezone?: string;
  } & Enrolment;
  switchToForm(): void;
  enrolmentPolicy: SessionPolicy;
  pausePolicyLoading: boolean;
  enrolmentPolicyError?: unknown;
}

type Option = {
  cardTitle: string;
  cardCTA: string;
  cardCTAHref?: string;
  cardAction?: () => void;
  promoText?: string;
  trackingClass: string;
  posthogEvent?: PostHogEvent;
};

export const CancelEntry = ({
  selectedEnrolment,
  switchToForm,
  pausePolicyLoading,
  enrolmentPolicyError,
  enrolmentPolicy,
}: Props) => {
  const {
    enrolmentSfid,
    enrolmentId,
    enrolmentStatus,
    student,
    subject,
    enrolmentPausedFrom,
    enrolmentPausedTo,
    resumeSessionDate,
    timezone,
    type,
  } = selectedEnrolment;
  const history = useHistory();
  const dispatch = useDispatch();
  const nextUpcomingSession = useSelector(getNextUpcomingSessionForEnrolment({ enrolmentId }));
  const hasIncentiveFebruaryRestartedPack = useSelector(hasIncentiveFebruaryRestartedPackCheck);
  const summerPromo = useSelector((state) => getSummerPromo(state, { screen: PATH_HUB_CR_CANCEL_ENROLMENT }));

  const { data: termsAndHolidays } = api.schoolTerm.holidays.useQuery();
  const currentOrUpcomingHoliday = getCurrentOrUpcomingSchoolHoliday((termsAndHolidays as SchoolHolidayTerm[]) || []);
  const isSummerHoliday = currentOrUpcomingHoliday?.type === 'Summer Holiday';
  const enrolmentCanBePaused = useCanPauseEnrolment(enrolmentSfid, enrolmentPolicy);

  if (saveCancellingPeriod() && hasIncentiveFebruaryRestartedPack) {
    let enrolmentsToSave = Cookies.get(COOKIE_SAVE_ENROLMENT);
    enrolmentsToSave = enrolmentsToSave ? JSON.parse(enrolmentsToSave) : [];
    enrolmentsToSave.push(enrolmentId);
    Cookies.set(COOKIE_SAVE_ENROLMENT, JSON.stringify(enrolmentsToSave), {
      expires: 7,
    });
  } else {
    Cookies.remove(COOKIE_SAVE_ENROLMENT);
  }

  const isActive = isEnrolmentActiveCheck({ enrolmentStatus });
  const isPaused = isEnrolmentPausedCheck({ enrolmentStatus });
  const isGroup = isGroupSessionCheck(type);
  const willBePaused = willEnrolmentBePausedCheck({
    enrolmentPausedFrom,
    enrolmentPausedTo,
  });

  const entryScreenOptions = useMemo(() => {
    const screenOptions: Option[] = [];
    /* enabled option for group enrolments in accordance with CRE-1019 */
    if (isPaused || willBePaused) {
      const resumeDate = moment(resumeSessionDate ?? nextUpcomingSession?.startTime)
        .tz(timezone)
        .format('MMM DD, YYYY');
      screenOptions.push({
        cardTitle: 'Change enrolment pause',
        cardCTA: `Resume on ${resumeDate} or with new schedule`,
        cardAction: () => {
          dispatch(fetchEnrolment({ enrolmentId }));
          posthog.capture('click_manage-enrolment_change-enrolment-pause');
          history.push(
            isSummerHoliday
              ? `${PATH_HUB_CR_PAUSE_SESSIONS}?token=${enrolmentId}`
              : `${PATH_HUB_ENROLMENT_MANAGE_PAUSE}?token=${enrolmentId}`
          );
        },
        promoText: summerPromo.promoText,
        trackingClass: TRACKING_CE.OPTION_MANAGE_PAUSE,
      });
    }

    screenOptions.push({
      cardTitle: 'Change schedule',
      cardCTA: isActive ? 'Make a change to ongoing schedule' : 'Make a change to ongoing schedule and restart',
      cardAction: () => {
        const page = isGroup
          ? `${PATH_HUB_ENROLMENT_CHANGE_SCHEDULE_GROUP}?token=${enrolmentId}`
          : `${PATH_HUB_ENROLMENT_CHANGE_SCHEDULE}?token=${enrolmentId}`;
        dispatch(fetchEnrolment({ enrolmentId }));
        posthog.capture('click_manage-enrolment_change-schedule');
        history.push(page);
      },
      promoText: isPaused || willBePaused ? summerPromo.promoText : '',
      trackingClass: TRACKING_CE.OPTION_CHANGE_SCHEDULE,
    });

    // NOTE, add change subject here
    screenOptions.push({
      cardTitle: 'Change tutor',
      cardCTA: 'Request a tutor change',
      cardAction: () => {
        history.push(`${PATH_HUB_CR_CHANGE_TUTOR}?token=${enrolmentId}`, { headerLabel: 'Change tutor' });
      },
      // cardCTAHref: `${PATH_HUB_CR_CHANGE_TUTOR}?token=${enrolmentId}`,
      trackingClass: TRACKING_CE.OPTION_CHANGE_TUTOR,
      posthogEvent: 'click_manage-enrolment_change-tutor',
    });

    /*
    Change content focus;
    This is only available to private enrolments
    */
    if (!isGroup) {
      screenOptions.push({
        cardTitle: 'Change content focus',
        cardCTA: 'Request to focus on specific topics or concepts',
        cardCTAHref: `${PATH_HUB_CR_CHANGE_CONTENT_FOCUS}?token=${enrolmentId}`,
        trackingClass: TRACKING_CE.OPTION_CHANGE_CONTENT,
        posthogEvent: 'click_manage-enrolment_change-content-focus',
      });
    }

    if (!isPaused && !willBePaused && enrolmentCanBePaused) {
      screenOptions.push({
        cardTitle: 'Pause enrolment',
        cardCTA: 'Take a break by pausing sessions',
        cardCTAHref: `${PATH_HUB_CR_PAUSE_SESSIONS}?token=${enrolmentId}`,
        trackingClass: TRACKING_CE.OPTION_PAUSE,
        posthogEvent: 'click_manage-enrolment_pause-enrolment',
      });
    }

    screenOptions.push({
      cardTitle: 'Cancel enrolment',
      cardCTA: "Let us know what didn't work for you",
      cardAction: () => {
        posthog.capture('click_manage-enrolment_cancel-enrolment');
        switchToForm();
      },
      trackingClass: TRACKING_CE.OPTION_CANCEL,
      posthogEvent: 'click_manage-enrolment_cancel-enrolment',
    });

    return screenOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enrolmentId,
    isActive,
    isPaused,
    isGroup,
    willBePaused,
    resumeSessionDate,
    nextUpcomingSession,
    timezone,
    summerPromo,
  ]);

  return (
    <EnrolmentLayout
      sectionHeading={
        <Link className="text-xs font-bold uppercase" to={PATH_HUB_SUPPORT}>
          Support
        </Link>
      }
      mainHeading="Manage enrolment"
      enrolmentId={enrolmentId}
      isLoading={pausePolicyLoading}
    >
      <div className="flex min-h-screen flex-col px-5 pb-10 md:px-10">
        <div className="container mr-auto flex flex-col gap-y-5 px-0 pb-16 md:gap-y-10">
          <IntentIntro>
            How can we help with{' '}
            <b>
              {student}'s {subject}
            </b>{' '}
            enrolment?
          </IntentIntro>
          {enrolmentPolicyError && (
            <div className="my-3">
              <ErrorBlock errorMsg="There was an error encountered while retrieving the pause policy." />
            </div>
          )}
          <div className="flex flex-col">
            <ul className="list-none">
              {entryScreenOptions.map((option) => {
                const {
                  cardTitle,
                  cardCTA,
                  cardCTAHref = null,
                  cardAction,
                  promoText = null,
                  trackingClass,
                  posthogEvent,
                } = option;
                const cardContent = (
                  <div className={trackingClass}>
                    <p className="mb-3 text-lg font-semibold text-grey-6">{cardTitle}</p>
                    <span className="text-lg font-normal text-grey-400">{cardCTA}</span>
                    {promoText && <PromoText text={promoText} />}
                  </div>
                );

                return (
                  <li key={cardTitle}>
                    <OptionCard
                      linkTo={cardCTAHref ?? undefined}
                      type={cardCTAHref ? ELEMENT_TYPES.LINK : ELEMENT_TYPES.BUTTON}
                      onClickHandler={!!cardAction ? cardAction : undefined}
                      capturePosthogEvent={!!posthogEvent ? () => posthog.capture(posthogEvent) : undefined}
                    >
                      <OptionCardContent>{cardContent}</OptionCardContent>
                    </OptionCard>
                  </li>
                );
              })}
            </ul>
          </div>
          {summerPromo.promoText && <SummerPromoLegalCopy />}
        </div>
      </div>
    </EnrolmentLayout>
  );
};

export default CancelEntry;
